import { useContext, useMemo, useState } from "react";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable"
import { ColDef, IServerSideDatasource } from "ag-grid-community";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { leadModelFiltersAtom } from "../../../state/leadModel";
import axios from "axios";
import { BorderColor, Save } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { AgGridCustomToolbarProps } from "../../../../../util/component/AgGridCustomToolbar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";

interface PrcCommercialSegmentDTO {
    id: string;
    name: string;
    // active: boolean;
}

interface LeadModelDTOs {
    id: string | null;
    category: string;
    commercialSegment: PrcCommercialSegmentDTO;
    legalEntity: string;
    leadModel: string;
}

function VehiclesLeadModelTable() {
    const { t } = useTranslation('pricing');
    const searchFilters = useRecoilValue(leadModelFiltersAtom);
    const [leadModelValues, setLeadModelValues] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [showSave, setShowSave] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [editContext, setEditContext] = useState<{ row: any; field: string } | null>(null);
    const [gridApi, setGridApi] = useState<any>(null);
    const { globalData, dispatchGlobalData } = useContext(GlobalContext);

    const canEdit = useMemo(() => {
        const { user } = globalData;
        return !!user.profiles.find((profile: any) => {
            return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_EDIT_LEAD_MODEL";
            });
        })
    }, [globalData]);

    function onGridReady(params: any) {
        setGridApi(params.api);
    }

    const columnDefs = useMemo<ColDef<any>[]>(() => [
        {
            field: 'category',
            headerName: t('leadModel.category'),
            minWidth: 120,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            },
        },
        {
            field: 'corporate',
            headerName: t('leadModel.corporate'),
            minWidth: 120,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellRenderer: (params: any) => {
                return <Box display="flex" alignItems="center" justifyContent="center" gap={1}> {params.value}

                    {canEdit && <IconButton
                        title={t('leadModel.edit')}
                        color="accent"
                        onClick={() => editClicked(params.data, params.colDef.field, params.rowIndex)}
                    >
                        <BorderColor />
                    </IconButton>}
                </Box>;
            }
        },
        {
            field: 'replacement',
            headerName: t('leadModel.replacement'),
            minWidth: 120,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellRenderer: (params: any) => {
                return <Box display="flex" alignItems="center" justifyContent="center" gap={1}> {params.value}

                    {canEdit && <IconButton
                        title={t('leadModel.edit')}
                        color="accent"
                        onClick={() => editClicked(params.data, params.colDef.field, params.rowIndex)}
                    >
                        <BorderColor />
                    </IconButton>}
                </Box>;
            }
        },
        {
            field: 'retail',
            headerName: t('leadModel.retail'),
            minWidth: 120,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellRenderer: (params: any) => {
                return <Box display="flex" alignItems="center" justifyContent="center" gap={1}> {params.value}

                    {canEdit && <IconButton
                        title={t('leadModel.edit')}
                        color="accent"
                        onClick={() => editClicked(params.data, params.colDef.field, params.rowIndex)}
                    >
                        <BorderColor />
                    </IconButton>}
                </Box>;
            }
        },
        {
            field: 'tourOperator',
            headerName: t('leadModel.tourOperator'),
            minWidth: 120,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellRenderer: (params: any) => {
                return <Box display="flex" alignItems="center" justifyContent="center" gap={1}> {params.value}

                    {canEdit && <IconButton
                        title={t('leadModel.edit')}
                        color="accent"
                        onClick={() => editClicked(params.data, params.colDef.field, params.rowIndex)}
                    >
                        <BorderColor />
                    </IconButton>}
                </Box>;
            }
        },
    ], [t, canEdit]);

    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: false,
        unSortIcon: true,
        minWidth: 130,
        wrapHeaderText: true,
        autoHeaderHeight: true,
    }), []);

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                const res = await axios.get("/pricing/lead-model/configuration?legalEntity=" + searchFilters.legalEntity?.name)
                    .then(res => res.data);

                const allLeadModelValues = res.rows.flatMap((row: any) => row.leadModelValues);

                setLeadModelValues(allLeadModelValues);


                const rowData = res.rows.map((r: any) => {
                    const base: any = {
                        category: r.rowHeader.label,
                        updatableVehicles: r.updatableVehicles
                    };
                    r.leadModelValues.forEach((v: any) => {
                        const segment = v.commercialSegment.id; // "RET", "COR", etc.
                        if (segment === "RET") base.retail = v.leadModel;
                        if (segment === "COR") base.corporate = v.leadModel;
                        if (segment === "REP") base.replacement = v.leadModel;
                        if (segment === "TOP") base.tourOperator = v.leadModel;
                    });
                    return base;
                });

                params.success({ rowData, rowCount: rowData.length });
            } catch (err) {
                params.fail();
                params.api.hideOverlay();
            }
        },
    }), [searchFilters]);

    function showEditPopup() {
        return <>
            {showEdit && (
                <Dialog open={showEdit} onClose={() => setShowEdit(false)} maxWidth="md" fullWidth>
                    <DialogContent dividers>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Typography variant="body1">
                                <b>{t("leadModel." + editContext?.field)} - {editContext?.row?.category}</b>
                            </Typography>
                            <TextField
                                select
                                value={editContext?.row?.[editContext?.field] ?? ""}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setEditContext((prev) =>
                                        prev ? { ...prev, row: { ...prev.row, [prev.field]: newValue } } : prev
                                    );
                                }}
                                SelectProps={{ native: true }}
                            >
                                {editContext?.row?.updatableVehicles?.map((v: string) => (
                                    <option key={v} value={v}>
                                        {v}
                                    </option>
                                ))}
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowEdit(false)}>{t("leadModel.cancel")}</Button>
                        <Button variant="contained" onClick={handleEditSave}>
                            {t("leadModel.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    }

    function showSavePopup() {
        return <>
            {showSave && (
                <Dialog open={showSave} onClose={() => setShowSave(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        <Typography>
                            {t("leadModel.savePopup.title")}
                        </Typography>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Typography>
                            {t("leadModel.savePopup.message")}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowSave(false)}>{t("leadModel.cancel")}</Button>
                        <Button variant="contained" onClick={handleSave}>
                            {t("leadModel.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    }

    function updateIsSaveDisabled() {
        const hasChanges = getUpdatedDtos(leadModelValues, getGridValue).length !== 0;
        setIsSaveDisabled(!hasChanges);
    }

    function editClicked(row: any, field: any, rowIndex: number) {
        setEditContext({ row: { ...row, rowIndex }, field });
        setShowEdit(true);
    }


    function handleEditSave() {
        if (!editContext || !gridApi) return;
        const { row, field } = editContext;
        const { rowIndex } = row;
        const newValue = row[field];

        const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
        if (rowNode) {
            rowNode.setDataValue(field, newValue);
        }

        setShowEdit(false);
        updateIsSaveDisabled();
    }

    function getUpdatedDtos(leadModelValues: any[], getGridValue: Function): LeadModelDTOs[] {
        const allRowNodes: any[] = [];

        gridApi.forEachNode((node: any) => {
            allRowNodes.push(node.data);
        });

        return leadModelValues
            .map((dto: any) => {
                const category = dto.category;
                const id = dto.commercialSegment.id;
                const newLeadModel = getGridValue(category, id, allRowNodes);

                if (!newLeadModel || newLeadModel === dto.leadModel) return null;

                return { ...dto, leadModel: newLeadModel };
            })
            .filter((dto): dto is LeadModelDTOs => dto !== null);
    }

    async function handleSave() {
        if (!leadModelValues || !gridApi) {
            throw ("handleSave error");
        }

        try {
            await axios.put(
                "pricing/lead-model/configuration?legalEntity=" + searchFilters.legalEntity?.name,
                getUpdatedDtos(leadModelValues, getGridValue)
            );

            gridApi.refreshServerSideStore({ purge: true });
            setShowSave(false);
            updateIsSaveDisabled();

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('dialogs.titles.success'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        } catch (err) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('dialogs.titles.error'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }

    function getGridValue(category: string, id: string, allRowNodes: any[]): string | undefined {
        const categoryMap: Record<string, string> = {
            M: 'Mini',
            E: 'Economy',
            C: 'Compact',
            I: 'Intermediate',
            S: 'Standard',
            F: 'Fullsize',
            P: 'Premium',
            X: 'Luxury',
        };

        const columnMap: Record<string, keyof typeof allRowNodes[0]> = {
            RET: "retail",
            COR: "corporate",
            REP: "replacement",
            TOP: "tourOperator",
        };

        const fullCategory = categoryMap[category] || category; // fallback if not mapped
        const column = columnMap[id];
        if (!column) return undefined;

        const row = allRowNodes.find(r => r?.category === fullCategory);
        return row?.[column];
    }

    function toolbarActions() {
        return (
            <Box px={2}>
                <IconButton title={t('leadModel.save')} color="accent" onClick={() => setShowSave(true)} disabled={isSaveDisabled}>
                    <Save />
                </IconButton>
            </Box>
        );
    }

    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderActions: toolbarActions
    }), [isSaveDisabled]);

    return (
        <>
            <AgGridCustomTable
                onGridReady={onGridReady}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource={serverSideDatasource}
                paginationPageSize={25}
                rowModelType="serverSide"
                suppressMultiSort
                suppressDragLeaveHidesColumns
                cacheBlockSize={25}
                enableCellTextSelection
                tooltipShowDelay={0}
                toolbar={toolbar}
            />
            {showEditPopup()}
            {showSavePopup()}
        </>
    )
}

export default VehiclesLeadModelTable;
